import React, { useCallback, useState } from "react";
import { Button, Tooltip } from "@mui/material";
import Icon from "@mdi/react";
import { mdiWeatherNight, mdiWhiteBalanceSunny, mdiMonitor, mdiUndoVariant, mdiCheck } from "@mdi/js";
import rootStyles from "../styles.module.scss";
import styles from "./styles.module.scss";
import { clsx } from "@ismael1361/utils";
import { NotistackServices } from "shared/services";
import { useThemeSettings, DEFAULT_PRIMARY, DEFAULT_SECONDARY, ThemeMode } from "shared/contexts";

const THEME_OPTIONS: Array<{ value: ThemeMode; label: string; icon: string; description: string }> = [
	{ value: "light", label: "Claro", icon: mdiWhiteBalanceSunny, description: "Tema com fundo claro" },
	{ value: "dark", label: "Escuro", icon: mdiWeatherNight, description: "Tema com fundo escuro" },
	{ value: "system", label: "Sistema", icon: mdiMonitor, description: "Segue o tema do dispositivo" },
];

const PRESET_PALETTES: Array<{ name: string; primary: string; secondary: string }> = [
	{ name: "Padrão", primary: "#1DB954", secondary: "#FF003C" },
	{ name: "Oceano", primary: "#0EA5E9", secondary: "#F97316" },
	{ name: "Violeta", primary: "#8B5CF6", secondary: "#EC4899" },
	{ name: "Dourado", primary: "#EAB308", secondary: "#6366F1" },
	{ name: "Coral", primary: "#F43F5E", secondary: "#06B6D4" },
	{ name: "Menta", primary: "#10B981", secondary: "#F59E0B" },
];

export const AppearancePage = () => {
	const { themeMode, resolvedMode, colors, setThemeMode, setPrimaryColor, setSecondaryColor, resetColors } = useThemeSettings();
	const [activePreset, setActivePreset] = useState<string | null>(() => {
		const matched = PRESET_PALETTES.find(
			(p) => p.primary.toUpperCase() === colors.primary.toUpperCase() && p.secondary.toUpperCase() === colors.secondary.toUpperCase(),
		);
		return matched?.name ?? null;
	});

	const handleThemeChange = useCallback(
		(mode: ThemeMode) => {
			setThemeMode(mode);
			// NotistackServices.showSuccessMessage(`Tema alterado para "${THEME_OPTIONS.find((o) => o.value === mode)?.label}"`);
		},
		[setThemeMode],
	);

	const handlePresetSelect = useCallback(
		(preset: (typeof PRESET_PALETTES)[number]) => {
			setPrimaryColor(preset.primary);
			setSecondaryColor(preset.secondary);
			setActivePreset(preset.name);
		},
		[setPrimaryColor, setSecondaryColor],
	);

	const handleReset = useCallback(() => {
		resetColors();
		setActivePreset("Padrão");
		// NotistackServices.showSuccessMessage("Cores restauradas para o padrão");
	}, [resetColors]);

	const isDefault =
		colors.primary.toUpperCase() === DEFAULT_PRIMARY.toUpperCase() && colors.secondary.toUpperCase() === DEFAULT_SECONDARY.toUpperCase();

	return (
		<div className={rootStyles.contentWrapper}>
			{/* ===== TEMA ===== */}
			<section className={rootStyles.section}>
				<div className={rootStyles.sectionHeader}>
					<div className={rootStyles.icon}>
						<Icon
							path={resolvedMode === "dark" ? mdiWeatherNight : mdiWhiteBalanceSunny}
							size={1.2}
						/>
					</div>
					<div className={rootStyles.titles}>
						<h3>Modo de Exibição</h3>
						<p>Escolha como a interface deve ser exibida.</p>
					</div>
				</div>

				<div className={styles.themeCards}>
					{THEME_OPTIONS.map((opt) => (
						<button
							key={opt.value}
							className={clsx(styles.themeCard, themeMode === opt.value && styles.active)}
							onClick={() => handleThemeChange(opt.value)}
							type="button"
						>
							<div
								className={styles.themeCardPreview}
								data-theme={opt.value}
								data-resolved={resolvedMode}
							>
								<div className={styles.previewSidebar} />
								<div className={styles.previewContent}>
									<div className={styles.previewLine} />
									<div className={styles.previewLineShort} />
									<div className={styles.previewBlock} />
								</div>
							</div>
							<div className={styles.themeCardInfo}>
								<Icon
									path={opt.icon}
									size={0.8}
								/>
								<span className={styles.themeLabel}>{opt.label}</span>
							</div>
							{themeMode === opt.value && (
								<div className={styles.activeCheck}>
									<Icon
										path={mdiCheck}
										size={0.65}
									/>
								</div>
							)}
						</button>
					))}
				</div>
			</section>

			{/* ===== CORES ===== */}
			<section className={rootStyles.section}>
				<div className={rootStyles.sectionHeader}>
					<div className={rootStyles.icon}>
						<div
							className={styles.colorDot}
							style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
						/>
					</div>
					<div className={rootStyles.titles}>
						<h3>Cores da Interface</h3>
						<p>Personalize as cores utilizadas nos botões, links e destaques.</p>
					</div>
				</div>

				<div
					className={rootStyles.card}
					style={{
						background: "transparent",
						border: "none",
						padding: 0,
					}}
				>
					<div className={styles.presetGrid}>
						{PRESET_PALETTES.map((preset) => (
							<Tooltip
								key={preset.name}
								title={preset.name}
								arrow
								placement="top"
							>
								<button
									className={clsx(styles.presetBtn, activePreset === preset.name && styles.active)}
									onClick={() => handlePresetSelect(preset)}
									type="button"
								>
									<div className={styles.presetColors}>
										<span style={{ backgroundColor: preset.primary }} />
										<span style={{ backgroundColor: preset.secondary }} />
									</div>
									<span className={styles.presetName}>{preset.name}</span>
								</button>
							</Tooltip>
						))}
					</div>
				</div>
			</section>
		</div>
	);
};
